import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { ChevronDown } from 'lucide-react';
import { Customer } from '@/types';
import { adminAPI } from '@/services/api';

interface CustomerAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userEmail?: string;
  onAssign: (customerId: number) => Promise<void>;
}

export const CustomerAssignDialog: React.FC<CustomerAssignDialogProps> = ({
  open,
  onOpenChange,
  userEmail,
  onAssign,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // 고객사 목록 불러오기
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await adminAPI.getActiveCustomers(0, 1000);
      setAllCustomers(response.customers || []);
    } catch (error: any) {
      console.error('Failed to fetch customers for assignment:', error);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchCustomers();
      setShowSuggestions(false);
    }
  }, [open, fetchCustomers]);

  // 필터링된 고객사 목록
  const filteredCustomers = searchQuery.trim() === '' 
    ? allCustomers // 검색어가 없으면 전체 목록
    : allCustomers.filter(customer =>
        customer.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.customer_code?.includes(searchQuery)
      );

  const handleAssign = async () => {
    if (!selectedCustomerId) return;
    try {
      await onAssign(selectedCustomerId);
      // 성공 시 다이얼로그 닫기 및 상태 초기화
      onOpenChange(false);
      setSelectedCustomerId(null);
      setSearchQuery('');
      setShowSuggestions(false);
    } catch (error) {
      // 에러는 상위에서 처리
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setSelectedCustomerId(null);
    setSearchQuery('');
    setShowSuggestions(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>고객사 할당</DialogTitle>
          <DialogDescription>
            {userEmail} 사용자에게 고객사를 할당합니다. (일반 사용자로 등록됩니다)
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="assign_customer_select">고객사 선택</Label>
            <div className="relative">
              <div className="flex items-center gap-0.5">
                <Input
                  ref={inputRef}
                  id="assign_customer_select"
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);
                    // 검색어가 변경되면 선택 해제
                    const selectedCustomer = allCustomers.find(c => c.id === selectedCustomerId);
                    if (selectedCustomer && value !== selectedCustomer.customer_name && value !== selectedCustomer.customer_code) {
                      setSelectedCustomerId(null);
                    }
                    // 검색어가 있든 없든 필터링된 목록 표시
                    setShowSuggestions(true);
                  }}
                  onFocus={() => {
                    // 포커스 시 항상 목록 표시 (검색어가 없으면 전체 목록)
                    setShowSuggestions(true);
                  }}
                  onBlur={(e) => {
                    // 풀다운 내부나 버튼을 클릭한 경우가 아니면 닫기
                    const relatedTarget = e.relatedTarget as HTMLElement;
                    if (
                      relatedTarget &&
                      (suggestionsRef.current?.contains(relatedTarget) ||
                       relatedTarget.closest('button[data-dropdown-toggle]'))
                    ) {
                      return;
                    }
                    // 약간의 지연을 두어 클릭 이벤트가 먼저 처리되도록
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                  placeholder="고객사명 또는 코드로 검색..."
                  className="w-full rounded-r-none"
                  autoFocus={false}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-l-none border-l-0 h-10 px-3"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowSuggestions(!showSuggestions);
                  }}
                  data-dropdown-toggle
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${showSuggestions ? 'rotate-180' : ''}`} />
                </Button>
              </div>
              {showSuggestions && filteredCustomers.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                  onMouseDown={(e) => {
                    // 풀다운 내부 클릭 시 onBlur가 발생하지 않도록
                    e.preventDefault();
                  }}
                >
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => {
                        setSelectedCustomerId(customer.id);
                        setSearchQuery(customer.customer_name);
                        setShowSuggestions(false);
                      }}
                      className={`px-4 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-200 last:border-b-0 ${
                        selectedCustomerId === customer.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="font-medium text-sm">
                        {customer.customer_name} ({customer.customer_code})
                      </div>
                      {customer.alias && (
                        <div className="text-xs text-gray-500">별칭: {customer.alias}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {showSuggestions && filteredCustomers.length === 0 && searchQuery.trim() && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                  <div className="px-4 py-2 text-sm text-gray-500 text-center">
                    검색 결과가 없습니다
                  </div>
                </div>
              )}
            </div>
            {selectedCustomerId && (
              <p className="text-xs text-green-600 mt-1">
                ✓ {allCustomers.find(c => c.id === selectedCustomerId)?.customer_name} 선택됨
              </p>
            )}
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              취소
            </Button>
            <Button
              type="button"
              onClick={handleAssign}
              disabled={!selectedCustomerId}
            >
              할당
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

